import { getQrMode } from "../../../api/qrWorkflowMode";
import React, { Component } from "react";
import Layout from "../../../Layout/layout";
import Helmet from "react-helmet";
import axios from "axios";
import { saveAs } from "file-saver";
import GridLoader from "react-spinners/GridLoader";
import { apiHost } from "../../../api/apiClient";
import "../../../styles/reports/checklistRunsReport.scss";

const FALLBACK_TZ = "Australia/Melbourne";

const getValue = (obj, key) => {
  if (!obj) return null;
  if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  const pascal = key.charAt(0).toUpperCase() + key.slice(1);
  return obj[pascal];
};

const hasText = (value) => String(value || "").trim() !== "";

const displayOdometer = (value, includeUnit = false) => {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return `${number.toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}${includeUnit ? " km" : ""}`;
};

const distanceReading = value => {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toLocaleString("en-AU")} km` : "—";
};

const isSystemLogout = run => getValue(run, "recordKind") !== "checklist"
  && String(getValue(run, "source") || "").toLowerCase() === "system"
  && String(getValue(run, "checklistType") || "").toLowerCase() === "post_trip";

const autoLogoutHours = run => {
  const match = String(getValue(run, "reason") || "").match(/automatically after\s+(\d+)\s+hours?/i);
  return match ? Number(match[1]) : null;
};

const autoLogoutMessage = run => {
  const hours = autoLogoutHours(run);
  if (hours) {
    return `Automatic logout is enabled by your organisation and was set to ${hours} ${hours === 1 ? "hour" : "hours"} when this event occurred. This driver session was closed automatically after that period. No Post-Trip odometer was entered, so a trip summary is not available. To change this setting, please contact the VisionTrak Support team.`;
  }
  return "This driver session was closed automatically by the system. No Post-Trip odometer was entered, so a trip summary is not available.";
};

const normalizeTz = (tz) => {
  const value = String(tz || "").trim();
  if (!value) return FALLBACK_TZ;
  try {
    new Intl.DateTimeFormat("en-AU", { timeZone: value }).format(new Date());
    return value;
  } catch (e) {
    return FALLBACK_TZ;
  }
};

const parseDate = (value) => {
  if (!value) return null;
  const text = String(value).trim();
  const mysqlMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (mysqlMatch) {
    return new Date(Date.UTC(
      Number(mysqlMatch[1]),
      Number(mysqlMatch[2]) - 1,
      Number(mysqlMatch[3]),
      Number(mysqlMatch[4]),
      Number(mysqlMatch[5]),
      Number(mysqlMatch[6])
    ));
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDbDate = (value, tz) => {
  const date = parseDate(value);
  if (!date) return value || "-";
  try {
    return new Intl.DateTimeFormat("en-AU", {
      timeZone: normalizeTz(tz),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(date);
  } catch (e) {
    return value || "-";
  }
};

const durationText = (start, end) => {
  const started = parseDate(start);
  const completed = parseDate(end);
  if (!started || !completed) return "-";
  const diff = Math.max(0, Math.floor((completed.getTime() - started.getTime()) / 1000));
  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;
  return hours > 0 ? `${hours}h ${minutes}m ${seconds}s` : `${minutes}m ${seconds}s`;
};

const statusClass = (status) => {
  const value = String(status || "").toLowerCase();
  if (value === "pass" || value === "yes") return "pass";
  if (value === "fail" || value === "no") return "fail";
  if (value === "logged") return "logged";
  if (value === "system") return "system";
  if (value === "incident") return "incident";
  return "muted";
};

const isCriticalItem = (item) => {
  const value = getValue(item, "critical");
  return value === true || Number(value) === 1 || String(value).toLowerCase() === "true";
};

const isCriticalFailure = (item) => {
  if (!isCriticalItem(item)) return false;
  const failureResponse = String(getValue(item, "criticalFailOn") || "").trim().toLowerCase();
  const actualResponse = String(getValue(item, "response") || "").trim().toLowerCase();
  return hasText(failureResponse) && actualResponse === failureResponse;
};

const itemResponseStatusClass = (item) => {
  if (isCriticalItem(item) && hasText(getValue(item, "criticalFailOn"))) {
    return isCriticalFailure(item) ? "fail" : "pass";
  }
  return statusClass(getValue(item, "response"));
};

const displayStatus = (row) => {
  const source = String(getValue(row, "source") || "").trim().toLowerCase();
  return source === "system" ? "system" : String(getValue(row, "status") || "").trim().toLowerCase();
};

const checklistLabel = (value) => {
  if (value === "pre_start") return "PRE-START";
  if (value === "post_trip") return "POST-TRIP";
  return value || "-";
};

const sourceLabel = (value) => {
  const source = String(value || "driver").toLowerCase();
  return source === "system" ? "SYSTEM" : "DRIVER";
};

const formattedDeclarationText = text => String(text || "").split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, index) => part.startsWith("**") && part.endsWith("**") ? <strong key={index}>{part.slice(2, -2)}</strong> : <React.Fragment key={index}>{part}</React.Fragment>);

const declarationText = (run) => {
  const snapshot = String(getValue(run, "driverDeclarationText") || "").trim();
  if (snapshot) return snapshot.replace(/\r\n/g, "\n").split(/\n+/).map(line => line.trim()).filter(Boolean);
  const checklistType = getValue(run, "checklistType");
  const preStart = String(checklistType || "").toLowerCase() === "pre_start";
  return [
    preStart
      ? "I declare that the responses provided in this pre-start checklist are true and correct to the best of my knowledge."
      : "I declare that the responses provided in this post-trip checklist are true and correct to the best of my knowledge.",
    preStart
      ? "I confirm that I have personally inspected the vehicle prior to operation, that it is in a safe and roadworthy condition, and that I am fit for duty and not impaired by alcohol, drugs, fatigue, or medication."
      : "I confirm that I have accurately reported the condition of the vehicle at the end of this shift, including any defects, damage, incidents, or safety issues observed during operation.",
    "I understand that providing false or misleading information may result in disciplinary action, void insurance coverage, and expose me to legal liability. This declaration forms part of the company’s Chain of Responsibility and safety obligations.",
  ];
};

const SPLIT_STORAGE_KEY = "driver_logs_checklist_split_pct";

class ChecklistRunsReport extends Component {
  constructor(props) {
    super(props);
    const savedSplit = Number(localStorage.getItem(SPLIT_STORAGE_KEY));
    this.state = {
      rows: [],
      selectedRun: null,
      selectedItems: [],
      selectedKey: "",
      isLoading: false,
      detailsLoading: false,
      isExporting: false,
      errorMessage: "",
      date: "",
      page: 1,
      pageSize: 100,
      hasMore: false,
      filters: {
        type: "all",
        status: "all",
        notes: "all",
        search: "",
      },
      splitPct: Number.isFinite(savedSplit) && savedSplit >= 40 && savedSplit <= 76 ? savedSplit : 62,
      resizing: false,
    };
    this.gridRef = React.createRef();
    this.tableWrapRef = React.createRef();
  }

  componentDidMount() {
    getQrMode().then(mode => this.setState({ qrServerTestMode: mode.testMode })).catch(() => this.setState({ errorMessage: "QR workflow mode is unavailable." }));
    this.loadRuns();
    document.addEventListener("mousemove", this.handleSplitterMove);
    document.addEventListener("mouseup", this.stopResize);
    document.addEventListener("mouseleave", this.stopResize);
  }

  componentWillUnmount() {
    document.removeEventListener("mousemove", this.handleSplitterMove);
    document.removeEventListener("mouseup", this.stopResize);
    document.removeEventListener("mouseleave", this.stopResize);
  }

  loadRuns = async (requestedPage = 1) => {
    const page = Number.isFinite(requestedPage) ? Math.max(1, requestedPage) : 1;
    this.setState({ isLoading: true, errorMessage: "", selectedRun: null, selectedItems: [], selectedKey: "" });
    try {
      const params = { page, pageSize: this.state.pageSize, test: Boolean(this.state.qrServerTestMode) };
      if (this.state.date) {
        params.date = this.state.date;
        params.timezone = localStorage.getItem("timeZoneID") || FALLBACK_TZ;
      }
      const response = await axios.get(`${apiHost}/api/Report/ChecklistRuns`, {
        params,
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });

      const rows = (response.data && response.data.rows ? response.data.rows : []).map((row) => ({
        ...row,
        rowKey: getValue(row, "rowKey") || `${getValue(row, "recordKind")}-${getValue(row, "id")}`,
      }));
      this.setState({
        rows,
        page: Number(response.data && response.data.page) || page,
        hasMore: Boolean(response.data && response.data.hasMore),
        isLoading: false,
      }, () => {
        if (this.tableWrapRef.current) this.tableWrapRef.current.scrollTop = 0;
      });
    } catch (e) {
      const message = e && e.response && e.response.data && e.response.data.error
        ? e.response.data.error
        : String(e);
      this.setState({ isLoading: false, errorMessage: message });
    }
  };

  loadDetails = async (run) => {
    const recordKind = getValue(run, "recordKind");
    const rowKey = getValue(run, "rowKey");
    this.setState({ selectedRun: run, selectedItems: [], selectedKey: rowKey || "", detailsLoading: recordKind === "checklist" });
    try {
      const config = { params: { test: Boolean(this.state.qrServerTestMode) }, headers: { Authorization: "Bearer " + localStorage.getItem("token") } };
      if (recordKind === "incident") {
        const response = await axios.get(`${apiHost}/api/Report/IncidentReports/${getValue(run, "id")}`, config), i = response.data || {};
        return this.setState({ selectedRun: { ...run, incidentDetails: i, photos: i.photos || [], driverDeclarationText: i.driver_declaration_text, driverDeclared: i.driver_declared, driverDeclaredAt: i.driver_declared_at }, selectedItems: [], detailsLoading: false });
      }
      const requests = [axios.get(`${apiHost}/api/Report/DriverLogShiftSummary/${recordKind}/${getValue(run, "id")}`, config)];
      if (recordKind === "checklist") requests.push(axios.get(`${apiHost}/api/Report/ChecklistRuns/${getValue(run, "id")}`, config));
      const [summaryResponse, detailResponse] = await Promise.all(requests);
      const detailedRun = detailResponse && detailResponse.data && detailResponse.data.run ? detailResponse.data.run : run;
      this.setState({
        selectedRun: { ...detailedRun, ...(summaryResponse.data || {}) },
        selectedItems: detailResponse && detailResponse.data ? detailResponse.data.items || [] : [],
        detailsLoading: false,
      });
    } catch (e) {
      this.setState({ detailsLoading: false, errorMessage: "Driver log details could not be loaded." });
    }
  };

  setFilter = (name, value) => {
    this.setState((state) => ({
      filters: {
        ...state.filters,
        [name]: value,
      },
    }));
  };

  clearDate = () => {
    this.setState({ date: "" }, () => this.loadRuns(1));
  };

  filterRows = (rows) => {
    const { filters } = this.state;
    const search = filters.search.trim().toLowerCase();
    return rows.filter((row) => {
      const type = getValue(row, "checklistType");
      const status = displayStatus(row);
      const notesCount = Number(getValue(row, "notesCount") || 0);

      if (filters.type !== "all" && type !== filters.type) return false;
      if (filters.status !== "all" && status !== filters.status) return false;
      if (filters.notes === "with_notes" && notesCount <= 0) return false;

      if (!search) return true;
      const haystack = [
        getValue(row, "id"),
        getValue(row, "driverName"),
        getValue(row, "assetName"),
        getValue(row, "assetFriendly"),
        getValue(row, "customerName"),
        getValue(row, "depotName"),
        getValue(row, "source"),
        getValue(row, "reason"),
      ].join(" ").toLowerCase();
      return haystack.indexOf(search) !== -1;
    });
  };

  getFilteredRows = () => this.filterRows(this.state.rows);

  exportExcel = async () => {
    this.setState({ isExporting: true, errorMessage: "" });
    try {
      const allRows = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const params = { page, pageSize: 500, test: Boolean(this.state.qrServerTestMode) };
        if (this.state.date) {
          params.date = this.state.date;
          params.timezone = localStorage.getItem("timeZoneID") || FALLBACK_TZ;
        }
        const response = await axios.get(`${apiHost}/api/Report/ChecklistRuns`, {
          params,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        allRows.push(...(response.data && response.data.rows ? response.data.rows : []));
        hasMore = Boolean(response.data && response.data.hasMore);
        page += 1;
      }

      const rows = this.filterRows(allRows);
      const headers = [
        "ID", "Record Type", "Completed", "Driver", "Driver ID", "Asset", "Asset ID",
        "Customer", "Depot", "Checklist/Event", "Status", "Source", "Reason", "Event Mode",
        "Odometer", "Items", "Notes", "Checklist Run ID", "Driver Declaration Confirmed",
        "Declaration Confirmed At", "Declaration Type", "Declaration Version", "Declaration Text",
      ];
      const csvValue = (value) => {
        let text = String(value == null ? "" : value);
        if (/^[=+\-@]/.test(text)) text = `'${text}`;
        return `"${text.replace(/"/g, '""')}"`;
      };
      const lines = [headers.map(csvValue).join(",")];
      rows.forEach((row) => {
        const tz = getValue(row, "depotTimezone");
        lines.push([
          getValue(row, "displayId") || getValue(row, "id"),
          getValue(row, "recordKind"),
          formatDbDate(getValue(row, "completedAt"), tz),
          getValue(row, "driverName"),
          getValue(row, "driverId"),
          this.assetDisplay(row),
          getValue(row, "assetId"),
          getValue(row, "customerName"),
          getValue(row, "depotName"),
          checklistLabel(getValue(row, "checklistType")),
          displayStatus(row).toUpperCase(),
          hasText(getValue(row, "source")) ? sourceLabel(getValue(row, "source")) : "",
          getValue(row, "reason"),
          getValue(row, "eventMode"),
          getValue(row, "odometer"),
          getValue(row, "responseCount") || 0,
          getValue(row, "notesCount") || 0,
          getValue(row, "checklistRunId"),
          getValue(row, "driverDeclared") ? "Yes" : "No",
          getValue(row, "driverDeclared") ? formatDbDate(getValue(row, "driverDeclaredAt"), tz) : "",
          getValue(row, "driverDeclarationType"),
          getValue(row, "driverDeclarationVersion"),
          getValue(row, "driverDeclarationText"),
        ].map(csvValue).join(","));
      });

      const fileDate = this.state.date || new Date().toISOString().slice(0, 10);
      saveAs(new Blob(["\ufeff", lines.join("\r\n")], { type: "text/csv;charset=utf-8" }), `${this.state.qrServerTestMode ? "qrtest-driver-logs" : "driver-logs"}-${fileDate}.csv`);
      this.setState({ isExporting: false });
    } catch (e) {
      this.setState({ isExporting: false, errorMessage: "Driver Logs Excel export could not be created." });
    }
  };

  incidentDetailRows = (run, tz) => {
    const i = getValue(run, "incidentDetails") || {};
    return [["Reference",i.incident_reference],["Acknowledgement",i.acknowledged_at ? (i.acknowledged_responsibility_text ? "Acknowledged — follow-up recorded" : "Acknowledged (legacy record)") : "Awaiting acknowledgement"],["Acknowledged by",i.acknowledged_by_name || i.acknowledged_by],["Acknowledged at",i.acknowledged_at && formatDbDate(i.acknowledged_at,tz)],["Incident occurred",i.incident_occurred_at && formatDbDate(i.incident_occurred_at,tz)],["Report submitted",i.submitted_at && formatDbDate(i.submitted_at,tz)],["Location",i.location_text],["Coordinates",i.latitude != null ? `${i.latitude}, ${i.longitude}` : ""],["What happened",i.description],["Injury",i.injury_involved],["Injury details",i.injury_details],["Medical assistance",i.medical_assistance],["Other vehicle",i.other_vehicle_involved],["Other driver",i.other_driver_name],["Other phone",i.other_driver_phone],["Other licence",i.other_driver_licence],["Registration",i.other_vehicle_registration],["Vehicle make/model",i.other_vehicle_make_model],["Company",i.other_party_company],["Insurer",i.other_party_insurer],["Property damage",i.property_damage],["Property details",i.property_damage_details],["Police attended",i.police_attended],["Alcohol PBT",i.police_alcohol_test],["Drug screening POFT",i.police_drug_test],["Police report number",i.police_reference],["Police details",i.police_details],["Required licence class",i.asset_required_licence_class],["Transport Safety applied",i.transport_safety_applied ? "Yes" : ""],["Carrying load",i.carrying_load],["Load affected",i.load_affected],["Load details",i.load_details],["Dangerous goods",i.dangerous_goods_involved],["Dangerous goods details",i.dangerous_goods_details],["Fatigue possible factor",i.fatigue_possible_factor],["Vehicle safe to continue",i.vehicle_safe_to_continue],["Towing / recovery required",i.towing_recovery_required],["Towing / recovery details",i.towing_recovery_details],["Post-incident testing required",i.post_incident_testing_required],["Post-incident testing status",i.post_incident_testing_status],["Post-incident testing details",i.post_incident_testing_details],["Odometer",i.odometer]].filter(([,value])=>value!==null&&value!==undefined&&value!=="");
  };

  exportPdf = () => {
    const run = this.state.selectedRun;
    if (!run) return;

    const tz = getValue(run, "depotTimezone");
    const isIncident = getValue(run, "recordKind") === "incident";
    const isEvent = getValue(run, "recordKind") !== "checklist";
    const items = this.state.selectedItems;
    const declared = getValue(run, "driverDeclared") === true || Number(getValue(run, "driverDeclared") || 0) === 1;
    const declaredAtValue = getValue(run, "driverDeclaredAt") || getValue(run, "completedAt");
    const declaredAt = formatDbDate(declaredAtValue, tz);
    const declarationParagraphs = declarationText(run);
    const detailColour = this.detailColourClass(run);
    const detailTitle = getValue(run, "displayId") || `${isEvent ? "Event" : "Run"} ${getValue(run, "id")}`;
    const systemLogout = isSystemLogout(run);
    const incident = getValue(run, "incidentDetails") || {};
    const incidentRows = this.incidentDetailRows(run, tz);
    const incidentPhotos = getValue(run, "photos") || [];
    const incidentHtml = isIncident ? `<section class="incident-detail"><h2>Incident Report</h2><dl>${incidentRows.map(([label,value]) => `<dt>${this.escapeHtml(label)}</dt><dd>${this.escapeHtml(value)}</dd>`).join("")}</dl>${incidentPhotos.length ? `<h2>Photos / Evidence</h2><div class="incident-photos">${incidentPhotos.map((photo,index) => `<img src="${this.escapeHtml(this.photoUrl(getValue(photo,"url")))}" alt="Incident evidence ${index+1}">`).join("")}</div>` : ""}</section>` : "";
    const shiftSummaryHtml = systemLogout
      ? `<section class="auto-logout-note"><h2>Automatic logout</h2><p>${this.escapeHtml(autoLogoutMessage(run))}</p></section>`
      : getValue(run, "applicable") ? `<section class="shift-summary"><h2>Post-Trip Shift Summary</h2><div><b>Start</b><span>${displayOdometer(getValue(run, "startOdometer"), true)}${getValue(run, "startAt") ? ` at ${formatDbDate(getValue(run, "startAt"), tz)}` : ""}</span></div><div><b>End</b><span>${displayOdometer(getValue(run, "endOdometer"), true)}${getValue(run, "endAt") ? ` at ${formatDbDate(getValue(run, "endAt"), tz)}` : ""}</span></div><div><b>Distance travelled</b><span>${distanceReading(getValue(run, "distance"))}</span></div></section>${getValue(run, "previousPostOdometer") !== null && getValue(run, "previousPostOdometer") !== undefined ? `<section class="shift-reference"><strong>Previous recorded Post-Trip — reference only</strong><b>${displayOdometer(getValue(run, "previousPostOdometer"), true)}${getValue(run, "previousPostAt") ? ` at ${formatDbDate(getValue(run, "previousPostAt"), tz)}` : ""}</b><span>This reading is not part of the current trip. A difference from the current Pre-Start may be valid if the vehicle moved between shifts for servicing, maintenance, repositioning, or other authorised use.</span></section>` : ""}` : "";
    const declarationHtml = declared ? `
      <section class="declaration">
        <h2>Driver Declaration</h2>
        ${declarationParagraphs.map(text => `<p>${this.formatDeclarationHtml(text)}</p>`).join("")}
        <div class="acknowledgement">&#10003; Declaration acknowledged by ${this.escapeHtml(getValue(run, "driverName") || "-")}</div>
        <div class="acknowledgement-time">${this.escapeHtml(declaredAt)} (${this.escapeHtml(normalizeTz(tz))})</div>
      </section>` : isEvent ? "" : `
      <section class="declaration missing">
        <h2>Declaration Missing</h2>
        <p>No driver declaration flag is stored for this run.</p>
      </section>`;
    const html = `
      <html>
      <head>
        <title>${isIncident ? "Incident Report" : isEvent ? "Driver Event" : "Checklist Run"} ${this.escapeHtml(isIncident ? getValue(run,"displayId") : getValue(run,"id"))}</title>
        <style>
          @page { margin: 14mm; }
          * { box-sizing: border-box; }
          body { font-family: Arial, sans-serif; color: #25344b; margin: 0; background: #fff; }
          .report-shell { overflow: hidden; border: 1px solid #dfe7f1; border-radius: 8px; }
          .report-head { display: flex; justify-content: space-between; align-items: center; padding: 13px 15px; background: #194991; color: #fff; font-size: 14px; font-weight: 700; }
          .report-head.pre, .report-head.pre-fail { background: #287d3c; }
          .report-head.post, .report-head.post-fail { background: #2368b5; }
          .report-head.pre-system, .report-head.post-system { background: #b96d0d; }
          .report-head.incident { background: #b91c1c; }
          .meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 7px 18px; padding: 14px; font-size: 13px; line-height: 1.4; border-bottom: 1px solid #dfe7f1; }
          .meta.pre { background: #e5f5e9; border-left: 4px solid #287d3c; }
          .meta.post { background: #e5f1ff; border-left: 4px solid #2368b5; }
          .meta.pre-fail, .meta.post-fail { background: #ffeded; border-left: 4px solid #c62828; }
          .meta.pre-system, .meta.post-system { background: #fff2d7; border-left: 4px solid #d68a18; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #d8e1ec; padding: 8px; text-align: left; font-size: 12px; vertical-align: top; }
          th { background: #eef4ff; color: #194991; }
          .critical-failure td { background: #fff6f6; }
          .response-pill { display: inline-block; border-radius: 999px; padding: 4px 8px; font-weight: 700; text-transform: uppercase; }
          .response-pill.pass { background: #e7f7ed; color: #1f7a3b; }
          .response-pill.fail { background: #fff1f1; color: #c62828; }
          .response-pill.muted { background: #eef1f4; color: #586274; }
          .declaration { margin: 14px; padding: 14px; border: 1px solid #cfd9e5; border-left: 4px solid #2e8b57; border-radius: 8px; background: #f8fafc; font-size: 12px; line-height: 1.5; }
          .declaration h2 { margin: 0 0 8px; font-size: 15px; color: #1f2937; }
          .declaration p { margin: 6px 0; }
          .declaration .acknowledgement { margin-top: 10px; color: #237a43; font-weight: 700; }
          .declaration .acknowledgement-time { margin-top: 3px; color: #667085; }
          .declaration.missing { border-color: #efcaca; background: #fff7f7; color: #9b2c2c; }
          .items { margin: 14px; width: calc(100% - 28px); }
          .shift-summary { margin: 14px 14px 0; padding: 14px; border: 1px solid #d0d9e5; border-left: 4px solid #2368b5; border-radius: 8px; font-size: 12px; }
          .shift-summary h2 { margin: 0 0 8px; font-size: 15px; }.shift-summary div { display: grid; grid-template-columns: 145px 1fr; gap: 10px; padding: 3px 0; }
          .shift-reference { display: flex; flex-direction: column; gap: 5px; margin: 12px 14px 0; padding: 12px 14px; border: 1px dashed #aebdce; border-radius: 8px; background: #f8fafc; color: #536174; font-size: 11px; line-height: 1.45; }
          .shift-reference strong { text-transform: uppercase; letter-spacing: .3px; color: #46566d; }.shift-reference b { color: #25344b; }
          .auto-logout-note { margin: 14px; padding: 14px; border: 1px solid #ead39d; border-left: 4px solid #b96d0d; border-radius: 8px; background: #fff9e9; font-size: 12px; line-height: 1.5; }
          .auto-logout-note h2 { margin: 0 0 7px; font-size: 15px; color: #754509; }.auto-logout-note p { margin: 0; }
          .incident-detail { margin: 14px; }.incident-detail h2 { margin: 12px 0 8px; color: #a71926; font-size: 16px; }.incident-detail dl { display: grid; grid-template-columns: 165px 1fr; margin: 0; border: 1px solid #efd1d1; }.incident-detail dt, .incident-detail dd { margin: 0; padding: 7px 9px; border-bottom: 1px solid #efdede; font-size: 11px; line-height: 1.4; }.incident-detail dt { background: #fff3f3; color: #667085; font-weight: 700; }.incident-detail dd { white-space: pre-wrap; }.incident-photos { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }.incident-photos img { width: 100%; max-height: 230px; object-fit: contain; border: 1px solid #dfe7f1; border-radius: 6px; break-inside: avoid; }
        </style>
      </head>
      <body>
        <div class="report-shell">
        <div class="report-head ${detailColour}"><span>${isIncident ? "Incident Details" : "Run Details"}</span><span>${this.escapeHtml(detailTitle)}</span></div>
        <div class="meta ${detailColour}">
          <div><strong>${isIncident ? "Reference" : isEvent ? "Event" : "Run"}:</strong> ${this.escapeHtml(isIncident ? getValue(run,"displayId") || incident.incident_reference : getValue(run,"id"))}</div>
          <div><strong>Status:</strong> ${getValue(run, "status") || "-"}</div>
          <div><strong>Customer:</strong> ${getValue(run, "customerName") || "-"}</div>
          <div><strong>Depot:</strong> ${getValue(run, "depotName") || "-"}</div>
          <div><strong>Driver:</strong> ${getValue(run, "driverName") || "-"}</div>
          <div><strong>Asset:</strong> ${this.assetDisplay(run)}</div>
          <div><strong>${isIncident ? "Incident type" : isEvent ? "Event Type" : "Checklist"}:</strong> ${this.escapeHtml(isIncident ? String(incident.incident_type || "").replace(/_/g," ") : checklistLabel(getValue(run, "checklistType")))} ${isEvent ? "" : getValue(run, "templateName") || ""}</div>
          <div><strong>Completed:</strong> ${formatDbDate(getValue(run, "completedAt"), tz)}</div>
          ${isEvent ? `<div><strong>Source:</strong> ${sourceLabel(getValue(run, "source"))}</div>` : `<div><strong>Duration:</strong> ${durationText(getValue(run, "startedAt"), getValue(run, "completedAt"))}</div>`}
          ${!systemLogout ? `<div><strong>Odometer:</strong> ${displayOdometer(getValue(run, "odometer"))}</div>` : ""}
          ${isEvent && !isIncident && hasText(getValue(run, "reason")) ? `<div><strong>Reason:</strong> ${this.escapeHtml(getValue(run, "reason"))}</div>` : ""}
        </div>
        ${isIncident ? incidentHtml + declarationHtml : isEvent ? declarationHtml : `<table class="items">
          <thead><tr><th>#</th><th>Checklist Item</th><th>Response</th><th>Notes</th><th>Photos</th></tr></thead>
          <tbody>
            ${items.map((item, index) => `
              <tr class="${isCriticalFailure(item) ? "critical-failure" : ""}">
                <td>${index + 1}</td>
                <td>${this.escapeHtml(getValue(item, "itemLabel") || "-")}</td>
                <td><span class="response-pill ${itemResponseStatusClass(item)}">${this.escapeHtml(getValue(item, "response") || "-")}</span></td>
                <td>${this.escapeHtml(getValue(item, "notes") || "-")}</td>
                <td>${(getValue(item, "attachments") || []).map(photo => `<img src="${this.escapeHtml(this.photoUrl(getValue(photo, "thumbnailUrl") || getValue(photo, "url")))}" style="width:110px;max-height:90px;object-fit:cover;border-radius:6px;margin:2px;" alt="Checklist photo">`).join("") || "-"}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>${declarationHtml}`}
        ${shiftSummaryHtml}
        </div>
      </body>
      </html>`;

    const frame = document.createElement("iframe");
    frame.setAttribute("title", "Driver log PDF export");
    frame.style.position = "fixed";
    frame.style.right = "0";
    frame.style.bottom = "0";
    frame.style.width = "0";
    frame.style.height = "0";
    frame.style.border = "0";
    document.body.appendChild(frame);
    const printWindow = frame.contentWindow;
    if (!printWindow) return document.body.removeChild(frame);
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    frame.onload = () => {
      const images = Array.from(printWindow.document.images || []);
      const loaded = images.map(image => image.complete ? Promise.resolve() : new Promise(resolve => { image.onload = resolve; image.onerror = resolve; }));
      Promise.race([Promise.all(loaded), new Promise(resolve => window.setTimeout(resolve, 3000))]).then(() => {
        printWindow.focus();
        printWindow.print();
        window.setTimeout(() => { if (frame.parentNode) frame.parentNode.removeChild(frame); }, 1000);
      });
    };
  };

  escapeHtml = (value) => String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  formatDeclarationHtml = value => this.escapeHtml(value).replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

  photoUrl = url => !url || /^https?:\/\//i.test(url) ? url : `${apiHost}${url.charAt(0) === "/" ? "" : "/"}${url}`;

  assetDisplay = (row) => {
    const assetName = getValue(row, "assetName") || getValue(row, "assetId") || "-";
    const friendly = getValue(row, "assetFriendly");
    return hasText(friendly) ? `${assetName} (${friendly})` : assetName;
  };

  renderBadge = (className, label) => (
    <span className={`checklist-badge ${className}`}>{label}</span>
  );

  renderRows = () => {
    const rows = this.getFilteredRows();
    if (!rows.length) {
      return (
        <tr>
          <td colSpan="9" className="checklist-empty">No records match the selected filters.</td>
        </tr>
      );
    }

    return rows.map((row) => {
      const tz = getValue(row, "depotTimezone");
      const rowKey = getValue(row, "rowKey");
      const type = getValue(row, "checklistType");
      const status = displayStatus(row);
      return (
        <tr
          key={rowKey}
          className={this.state.selectedKey === rowKey ? "active" : ""}
          onClick={() => this.loadDetails(row)}
        >
          <td>{getValue(row, "displayId") || getValue(row, "id")}</td>
          <td>{formatDbDate(getValue(row, "completedAt"), tz)}</td>
          <td>{getValue(row, "driverName") || "-"}</td>
          <td title={getValue(row, "assetId") || ""}>{this.assetDisplay(row)}</td>
          <td>{getValue(row, "depotName") || "-"}</td>
          <td>{this.renderBadge(getValue(row, "recordKind") === "incident" ? "incident" : type === "post_trip" ? "post" : type === "pre_start" ? "pre" : "muted", getValue(row, "recordKind") === "incident" ? "NONE" : checklistLabel(type))}</td>
          <td>{this.renderBadge(statusClass(status), String(status || "-").toUpperCase())}</td>
          <td>{getValue(row, "responseCount") || 0}</td>
          <td>{getValue(row, "notesCount") || 0}</td>
        </tr>
      );
    });
  };

  renderIncidentDetail = (run, tz) => {
    const photos = getValue(run, "photos") || [];
    const rows = this.incidentDetailRows(run, tz);
    return <><div className="incident-log-detail"><h3>Incident Report</h3><dl>{rows.map(([label,value])=><React.Fragment key={label}><dt>{label}</dt><dd>{String(value)}</dd></React.Fragment>)}</dl>{photos.length > 0 && <section><h4>Photos</h4><div className="checklist-report-photos">{photos.map((photo,index)=><a key={photo.id||index} href={photo.url} target="_blank" rel="noopener noreferrer"><img src={photo.thumbnailUrl||photo.url} alt={`Incident evidence ${index+1}`} /></a>)}</div></section>}</div>{this.renderDeclaration(run,tz)}</>;
  };

  renderDetail = () => {
    const run = this.state.selectedRun;
    if (!run) {
      return <div className="checklist-empty detail-empty">Select a run to view checklist responses.</div>;
    }

    const tz = getValue(run, "depotTimezone");
    if (getValue(run, "recordKind") === "incident") return this.renderIncidentDetail(run, tz);
    const isEvent = getValue(run, "recordKind") !== "checklist";
    const detailColour = this.detailColourClass(run);
    return (
      <>
        <div className={`checklist-detail-meta ${detailColour}`}>
          <div><strong>Customer:</strong> {getValue(run, "customerName") || "-"}</div>
          <div><strong>Depot:</strong> {getValue(run, "depotName") || "-"}</div>
          <div><strong>Driver:</strong> {getValue(run, "driverName") || "-"}</div>
          <div><strong>Asset:</strong> {this.assetDisplay(run)}</div>
          <div><strong>Completed:</strong> {formatDbDate(getValue(run, "completedAt"), tz)}</div>
          <div><strong>{isEvent ? "Event" : "Checklist"}:</strong> {checklistLabel(getValue(run, "checklistType"))} {getValue(run, "templateName") || ""}</div>
          <div><strong>Status:</strong> {displayStatus(run).toUpperCase() || "-"}</div>
          {!isSystemLogout(run) && <div><strong>Odometer:</strong> {displayOdometer(getValue(run, "odometer"))}</div>}
          {isEvent && <div><strong>Source:</strong> {sourceLabel(getValue(run, "source"))}</div>}
          {isEvent && hasText(getValue(run, "reason")) && <div><strong>Reason:</strong> {getValue(run, "reason")}</div>}
          {!isEvent && <div><strong>Duration:</strong> {durationText(getValue(run, "startedAt"), getValue(run, "completedAt"))}</div>}
        </div>
        {isEvent && getValue(run, "driverDeclared") && this.renderDeclaration(run, tz)}
        {!isEvent && this.renderChecklistItems()}
        {!isEvent && this.renderDeclaration(run, tz)}
        {this.renderShiftSummary(run, tz)}
      </>
    );
  };

  renderShiftSummary = (run, tz) => {
    if (isSystemLogout(run)) {
      return <div className="driver-auto-logout-note"><strong>Automatic logout</strong><span>{autoLogoutMessage(run)}</span></div>;
    }
    if (!getValue(run, "applicable")) return null;
    const startOdometer = getValue(run, "startOdometer"), endOdometer = getValue(run, "endOdometer"), distance = getValue(run, "distance");
    const previousOdometer = getValue(run, "previousPostOdometer"), previousAt = getValue(run, "previousPostAt");
    return <>
      <div className="driver-shift-summary"><strong>Post-Trip Shift Summary</strong><div><b>Start</b><span>{displayOdometer(startOdometer, true)}{getValue(run, "startAt") && ` at ${formatDbDate(getValue(run, "startAt"), tz)}`}</span></div><div><b>End</b><span>{displayOdometer(endOdometer, true)}{getValue(run, "endAt") && ` at ${formatDbDate(getValue(run, "endAt"), tz)}`}</span></div><div><b>Distance travelled</b><span>{distanceReading(distance)}</span></div></div>
      {previousOdometer !== null && previousOdometer !== undefined && <div className="driver-shift-reference"><strong>Previous recorded Post-Trip — reference only</strong><b>{displayOdometer(previousOdometer, true)}{previousAt && ` at ${formatDbDate(previousAt, tz)}`}</b><span>This reading is not part of the current trip. A difference from the current Pre-Start may be valid if the vehicle moved between shifts for servicing, maintenance, repositioning, or other authorised use.</span></div>}
    </>;
  };

  renderChecklistItems = () => {
    if (this.state.detailsLoading) {
      return (
        <div className="checklist-detail-loading">
          <GridLoader size={7} color={"#222222"} loading />
          <span>Loading checklist responses</span>
        </div>
      );
    }

    if (!this.state.selectedItems.length) {
      return <div className="checklist-empty detail-empty">No checklist responses were stored for this run.</div>;
    }

    return (
      <div className="checklist-items-wrap">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Checklist Item</th>
              <th>Response</th>
              <th>Notes</th>
              <th>Photos</th>
            </tr>
          </thead>
          <tbody>
            {this.state.selectedItems.map((item, index) => (
              <tr key={`${getValue(item, "itemId")}-${index}`} className={isCriticalFailure(item) ? "critical-failure" : ""}>
                <td>{index + 1}</td>
                <td>
                  {getValue(item, "itemLabel") || "-"}
                  {isCriticalItem(item) && this.renderBadge("critical", "CRITICAL")}
                </td>
                <td>{this.renderBadge(itemResponseStatusClass(item), String(getValue(item, "response") || "-").toUpperCase())}</td>
                <td>{hasText(getValue(item, "notes")) ? <span className="notes-text">{getValue(item, "notes")}</span> : "-"}</td>
                <td>{(getValue(item, "attachments") || []).length ? <div className="checklist-report-photos">{(getValue(item, "attachments") || []).map((photo, photoIndex) => <a key={getValue(photo, "id") || photoIndex} href={this.photoUrl(getValue(photo, "url"))} target="_blank" rel="noopener noreferrer"><img src={this.photoUrl(getValue(photo, "thumbnailUrl") || getValue(photo, "url"))} alt={`Checklist evidence ${photoIndex + 1}`} /></a>)}</div> : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  renderDeclaration = (run, tz) => {
    const declared = getValue(run, "driverDeclared") === true || Number(getValue(run, "driverDeclared") || 0) === 1;
    const declaredAt = getValue(run, "driverDeclaredAt") || getValue(run, "completedAt");
    const paragraphs = declarationText(run);
    return (
      <div className={`checklist-declaration ${declared ? "acknowledged" : "missing"}`}>
        {declared ? (
          <>
            <strong>Driver Declaration</strong>
            {paragraphs.map((text, index) => <p key={index}>{formattedDeclarationText(text)}</p>)}
            <div className="declaration-acknowledgement">✓ Declaration acknowledged by {getValue(run, "driverName") || "-"}</div>
            <div className="declaration-time">{formatDbDate(declaredAt, tz)} ({normalizeTz(tz)})</div>
          </>
        ) : (
          <><strong>Declaration Missing</strong><p>No driver declaration flag is stored for this run.</p></>
        )}
      </div>
    );
  };

  renderFilterButton = (group, value, label) => (
    <button
      type="button"
      className={this.state.filters[group] === value ? "active" : ""}
      onClick={() => this.setFilter(group, value)}
    >
      {label}
    </button>
  );

  detailColourClass = run => {
    if (!run) return "";
    if (getValue(run, "recordKind") === "incident") return "incident";
    const type = getValue(run, "checklistType") === "pre_start" ? "pre" : "post";
    if (displayStatus(run) === "system") return "post-system";
    return displayStatus(run) === "fail" ? `${type}-fail` : type;
  };

  startResize = (event) => {
    event.preventDefault();
    this.setState({ resizing: true });
  };

  stopResize = () => {
    if (this.state.resizing) {
      this.setState({ resizing: false });
    }
  };

  handleSplitterMove = (event) => {
    if (!this.state.resizing || !this.gridRef.current) {
      return;
    }

    const rect = this.gridRef.current.getBoundingClientRect();
    if (!rect.width) {
      return;
    }

    const pct = ((event.clientX - rect.left) / rect.width) * 100;
    const clamped = Math.max(40, Math.min(76, pct));
    localStorage.setItem(SPLIT_STORAGE_KEY, String(clamped));
    this.setState({ splitPct: clamped });
  };

  render() {
    const rows = this.getFilteredRows();
    const selected = this.state.selectedRun;
    const canExport = Boolean(selected);

    return (
      <Layout>
        <Helmet>
          <title>{this.state.qrServerTestMode ? "QR Test Driver Logs" : "Driver Logs"}</title>
        </Helmet>
        <div className="contentWrapper contentWrapperSmoothRelease report-modern-page checklist-runs-page">
          <div className="reportHeaderTile">
            <div className="headingContainer report-modern-header">
              <div>
                <h2 className="mainHeading">{this.state.qrServerTestMode ? "QR Test Driver Logs" : "Driver Logs"}</h2>
                <p>{this.state.qrServerTestMode ? "TEST DATA ONLY — records saved in the isolated QRTEST tables." : "Checklist runs and odometer-only driver logs for assets you can access."}</p>
              </div>
            </div>

            <div className="checklist-toolbar inputFormGroup reportPickerGrid reportPickerToolbar">
              <div className="checklist-filter-group">
                <span>Checklist</span>
                {this.renderFilterButton("type", "all", "All")}
                {this.renderFilterButton("type", "pre_start", "Pre-Start")}
                {this.renderFilterButton("type", "post_trip", "Post-Trip")}
                {this.renderFilterButton("type", "none", "None")}
              </div>
              <div className="checklist-filter-group">
                <span>Status</span>
                {this.renderFilterButton("status", "all", "All")}
                {this.renderFilterButton("status", "pass", "PASS")}
                {this.renderFilterButton("status", "fail", "FAIL")}
                {this.renderFilterButton("status", "logged", "LOGGED")}
                {this.renderFilterButton("status", "system", "SYSTEM")}
                {this.renderFilterButton("status", "incident", "INCIDENT")}
              </div>
              <div className="checklist-filter-group">
                <span>Notes</span>
                {this.renderFilterButton("notes", "all", "All")}
                {this.renderFilterButton("notes", "with_notes", "With Notes")}
              </div>
              <div className="checklist-filter-group">
                <span>Date</span>
                <input
                  type="date"
                  value={this.state.date}
                  onChange={(event) => this.setState({ date: event.target.value })}
                />
                <button type="button" onClick={() => this.loadRuns(1)}>Run Search</button>
                <button type="button" onClick={this.clearDate}>Latest 100</button>
              </div>
              <div className="checklist-filter-group search">
                <span>Search</span>
                <input
                  type="search"
                  placeholder="Run ID, driver, asset, customer, depot"
                  value={this.state.filters.search}
                  onChange={(event) => this.setFilter("search", event.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="reportBody">
            {this.state.errorMessage && <div className="checklist-error">{this.state.errorMessage}</div>}

            <div
              ref={this.gridRef}
              className={this.state.resizing ? "checklist-report-grid resizing" : "checklist-report-grid"}
              style={{
                gridTemplateColumns: `${this.state.splitPct}% 12px minmax(320px, 1fr)`,
              }}
            >
              <section className="checklist-panel">
                <div className="checklist-panel-head">
                  <strong>Runs</strong>
                  <div>
                    <button type="button" disabled={this.state.isExporting} onClick={this.exportExcel}>
                      {this.state.isExporting ? "Exporting..." : "Export Excel"}
                    </button>
                    <span>{this.state.isLoading ? "Loading..." : `${rows.length} records · Page ${this.state.page}`}</span>
                  </div>
                </div>
                <div ref={this.tableWrapRef} className="checklist-table-wrap">
                  {this.state.isLoading ? (
                    <div className="checklist-detail-loading">
                      <GridLoader size={7} color={"#222222"} loading />
                      <span>Loading runs</span>
                    </div>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Completed</th>
                          <th>Driver</th>
                          <th>Asset</th>
                          <th>Depot</th>
                          <th>Checklist</th>
                          <th>Status</th>
                          <th>Items</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>{this.renderRows()}</tbody>
                    </table>
                  )}
                </div>
                <div className="checklist-pagination">
                  <button
                    type="button"
                    disabled={this.state.isLoading || this.state.page <= 1}
                    onClick={() => this.loadRuns(this.state.page - 1)}
                  >
                    Previous 100
                  </button>
                  <span>Page {this.state.page}</span>
                  <button
                    type="button"
                    disabled={this.state.isLoading || !this.state.hasMore}
                    onClick={() => this.loadRuns(this.state.page + 1)}
                  >
                    Next 100
                  </button>
                </div>
              </section>

              <button
                type="button"
                className="checklist-splitter"
                onMouseDown={this.startResize}
                aria-label="Resize report panels"
                title="Drag to resize panels"
              />

              <section className="checklist-panel detail">
                <div className={`checklist-panel-head ${this.detailColourClass(selected)}`}>
                  <strong>{selected && getValue(selected, "recordKind") === "incident" ? "Incident Details" : "Run Details"}</strong>
                  <div>
                    <button type="button" disabled={!canExport} onClick={this.exportPdf}>Export PDF</button>
                    <span>{selected ? getValue(selected, "displayId") || "Selected" : "Select a run"}</span>
                  </div>
                </div>
                {this.renderDetail()}
              </section>
            </div>
          </div>
        </div>
      </Layout>
    );
  }
}

export default ChecklistRunsReport;
